import { createSelector } from '@reduxjs/toolkit';
import { getYear, subYears } from 'date-fns';

import { BOOKKEEPING_PRODUCT_CODES_ARR } from '_constants/products';
import { getCompanyName } from '_helpers/order';
import { getBusinessTaxFilingProduct, getPersonalTaxFilingProduct, parseTaxReports } from '_helpers/taxFilingInfo';

export const selectActiveOrderId = (state) => state.order.activeOrderId;
export const selectData = (state) => state.order.data;
export const selectError = (state) => state.order.error;
export const selectLoading = (state) => state.order.loading;

export const selectAccountManagerDetails = createSelector([selectData], (data) => data?.accountManager);

export const selectCategoryByProductId = (productId) =>
  createSelector([selectData], (data) => {
    return data?.products?.find(({ id }) => id === productId)?.code?.category;
  });

export const selectActiveProductCodes = createSelector([selectData], (data) =>
  data?.productCodes?.map(({ code }) => code)
);

export const selectActiveMainProductId = createSelector([selectData], (data) => {
  return data?.products?.find(({ code: { code, main } }) => BOOKKEEPING_PRODUCT_CODES_ARR.includes(code) && main)?.id;
});

export const selectActiveMainProductSource = createSelector(
  [selectData, selectActiveMainProductId],
  (data, activeProductId) => {
    return data?.products?.find(({ id }) => id === activeProductId)?.source;
  }
);

export const selectActiveBusinessTaxFilingProduct = createSelector([selectData], (data) =>
  getBusinessTaxFilingProduct(data)
);

export const selectActiveBusinessTaxFilingProductId = createSelector(
  [selectActiveBusinessTaxFilingProduct],
  (product) => product?.id
);

export const selectActivePersonalTaxFilingProduct = createSelector([selectData], (data) =>
  getPersonalTaxFilingProduct(data)
);

export const selectActivePersonalTaxFilingProductId = createSelector(
  [selectActivePersonalTaxFilingProduct],
  (product) => product?.id
);

export const selectTaxFilingStatuses = createSelector([selectData], (data) => {
  const businessTaxFilingProduct = getBusinessTaxFilingProduct(data);
  const personalTaxFilingProduct = getPersonalTaxFilingProduct(data);

  const parsedBusinessTaxReports = parseTaxReports(businessTaxFilingProduct?.taxReports);
  const parsedPersonalTaxReports = parseTaxReports(personalTaxFilingProduct?.taxReports);

  const prevYearTag = getYear(subYears(new Date(), 1));

  const prevYearBusinessTaxReport = parsedBusinessTaxReports.reports[prevYearTag] || {};
  const prevYearPersonalTaxReport = parsedPersonalTaxReports.reports[prevYearTag] || {};

  return {
    business: prevYearBusinessTaxReport.status,
    personal: prevYearPersonalTaxReport.status,
  };
});

export const selectCompanyName = createSelector([selectData], (data) => {
  return getCompanyName(data);
});
